import { CUSTOMER_FAMILY_ROLE, CUSTOMER_SELF_ROLE, FAMILY_ROLE } from '@careapp-group/core';
import { createSelector } from '@ngrx/store';
import { auth } from '../auth/auth.selectors';
import {
  State as AppState,
  CUSTOMERS,
  getEntities,
  getSelected,
  ORGANISATIONS,
} from '../reducers/index';
import { CustomerDto, OrganisationDto } from '@careapp-group/ng-sdk';

export const getCustomersState = (state: AppState) => state.customers;

// Returns the CustomerDto of any customer the current user is acting as.
export const getSelfCustomer = createSelector(
  auth.getLoggedInUser,
  getCustomersState,
  (user, state) => {
    if (!user) {
      return null;
    }
    const customerSelfId = state.ids.find((id) => {
      return state?.entities[id]?.roles?.includes(CUSTOMER_SELF_ROLE) ?? false;
    });
    if (!!customerSelfId) {
      return state.entities[customerSelfId];
    }
    return null;
  }
);

export const getCustomerPaginators = createSelector(getCustomersState, (state) => {
  if (state != null && state.paginators != null) {
    return state.paginators;
  }
  return null;
});

export const getSelectedCustomerOrganisationId = createSelector(getCustomersState, (state) => {
  if (state != null && state?.selectedId != null && state.ids.includes(state.selectedId)) {
    return state.entities[state.selectedId]?.organisationId;
  }
  return null;
});

export const userHasFilesForSelectedCustomer = createSelector(
  getSelected(CUSTOMERS),
  getEntities(ORGANISATIONS),
  (selectedCustomer: CustomerDto, orgs: { [id: string]: OrganisationDto }) => {
    if (selectedCustomer != null) {
      return (
        // staff roles are ok
        !orgs[selectedCustomer.organisationId]?.permissions
          .map((p) => p.role)
          .includes(FAMILY_ROLE) ||
        // if they are family, they can't be a follower
        (selectedCustomer.permissions?.length > 0 &&
          !selectedCustomer.permissions?.map((p) => p.role).includes(CUSTOMER_FAMILY_ROLE))
      );
    }
    return false;
  }
);

// Where a selected customer exists, only returns roles for that customer.
export const getCustomerRoles = createSelector(getCustomersState, (state) => {
  return (state.ids ?? [])
    .filter((id) => state.entities[id] != null)
    .map((id) =>
      state.entities[id].permissions
        .filter(
          // Only return enabled customer roles. If a selected customer exists, scope to that.
          (p) =>
            p.enabled === true &&
            p.modelName == 'Customer' &&
            (state.selectedId != null ? p.modelId == state.selectedId : true)
        )
        .map((p) => p.role)
    );
});
